import { Chatbox, Session } from 'talkjs/all';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';

export interface User {
  id?: string | number | undefined;
  name?: string | undefined;
  email?: string | null | undefined;
  custom?: object | undefined;
  role?: string | undefined;
}

/**
 * Defines possible instance IDs for TalkJS chatboxes.
 * - 'main': The primary chat instance, most users will only ever see these
 * - anything else: For more specific use cases where multiple chat instances are needed,
 * usually the secondary chat from main
 */
export type InstanceID = 'main' | 'collaborator' | 'agencyShareChat' | 'alerts';

// Keep the DOM and the API instances in the module scope
let talkSession: Session | null = null;
let sessionPromise: Promise<Session> | null = null;
const sessionContainers = new Map<string, HTMLDivElement>();
const talkChats = new Map<string, Chatbox>();
let showroom: HTMLDivElement | null = null;

export const initializeSession = async (user = {} as User) => {
  if (talkSession) return talkSession;
  if (sessionPromise) return await sessionPromise;

  sessionPromise = (async () => {
    const session = await createTalkSession(user);
    session.user(createTalkUser(user));
    talkSession = session;
    return session;
  })();

  return await sessionPromise;
};

interface GetThemedChatInterface {
  theme: string;
  user?: User;
  instanceID: InstanceID;
}

const getRegistryKey = (theme: string, instanceId: InstanceID) =>
  `${theme}_${instanceId}`;

export const getThemedChat = async ({
  theme,
  user,
  instanceID,
}: GetThemedChatInterface) => {
  const session = await initializeSession(user);
  const key = getRegistryKey(theme, instanceID);

  if (talkChats.has(key)) {
    return {
      container: sessionContainers.get(key)!,
      chat: talkChats.get(key)!,
    };
  }

  if (!showroom) {
    showroom = document.createElement('div');
    showroom.id = 'talkjs-showroom';
    document.body.appendChild(showroom);
  }

  const container = document.createElement('div');
  container.id = `talkjs-container-${key}`;

  Object.assign(container.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '0',
    height: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
  });

  showroom.appendChild(container);

  const messageFieldVisibility =
    instanceID === 'collaborator' || instanceID === 'agencyShareChat'
      ? true
      : false;

  const chat = session.createChatbox({
    theme: theme,
    showChatHeader: false,
    messageField: {
      visible: messageFieldVisibility,
      placeholder: 'Type Message',
    },
    messageFilter: { custom: { tf911ConversationID: '!exists' } },
  });

  sessionContainers.set(key, container);
  talkChats.set(key, chat);

  await chat.mount(container);

  return { container, chat };
};

export const getTalkInstances = (
  theme: string,
  instanceID = 'main' as InstanceID,
) => {
  const key = getRegistryKey(theme, instanceID);
  return {
    session: talkSession,
    chat: talkChats.get(key) || null,
    container: sessionContainers.get(key) || null,
  };
};
